import { ApplicationCode } from "constants/auth";

import { Suspense, useCallback, useMemo } from "react";

import { useMsal } from "@azure/msal-react";
import { Layout } from "components";
import { AppLanguages, Paths } from "configs";
import { confirm } from "contexts";
import Authorization from "features/authorization/Authorization";
import { NotificationRoutes } from "features/notification/routes";
import { ReportManagementList } from "features/reportManagement/components/ReportManagementList";
import { ReportDistributionDetailRoutes } from "features/reportManagement/routes";
import { RequestRoutes } from "features/requestManagement/routes";
import { ResetPasswordRoutes } from "features/resetPassword/routes";
import { SignContractRoutes } from "features/signContract/routes";
import { useLogout, useUser } from "libs/auth";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "stores";
import { systemSlice } from "stores/System";
import storage from "utils/storage";

import { SidebarBottomMenuRoutes, SidebarMenuRoutes } from "./sidebar";

const App = () => {
  const { instance } = useMsal();
  const { data, isLoading } = useUser();
  const dispatch = useAppDispatch();
  const { mutate: logout } = useLogout();

  const sidebarMenuBasedOnPermissions = useMemo(() => {
    return SidebarMenuRoutes;
  }, []);
  const sidebarBottomMenuBasedOnPermissions = useMemo(() => {
    return SidebarBottomMenuRoutes;
  }, []);

  const { language } = useAppSelector((state) => state.system);

  const { t } = useTranslation();

  const handleLogout = useCallback(() => {
    confirm({ title: t("Logout"), content: t("Do you want to logout?") })
      .then(() => {
        instance.logoutRedirect();
        // eslint-disable-next-line
        logout({});
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLanguageChange = useCallback((value: string) => {
    dispatch(systemSlice.actions.changeLanguage(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <Layout
        menu={sidebarMenuBasedOnPermissions}
        bottomMenu={sidebarBottomMenuBasedOnPermissions}
        userInfo={{
          email: `${data?.email as string}`,
          name: `${data?.fullName as string}`,
        }}
        languages={AppLanguages}
        defaultLanguage={language}
        onLogout={handleLogout}
        onLanguageChange={handleLanguageChange}
      />
    </Suspense>
  );
};

export const ProtectedRoutes = [
  {
    path: "/",
    element: <App />,
    children: [
      ...RequestRoutes,
      ...ResetPasswordRoutes,
      ...NotificationRoutes,
      { path: "*", element: <Navigate to="." /> },
      {
        path: "/",
        element:
          storage.getPermissionsMenu()?.includes(ApplicationCode.Request) &&
          !storage.getPermissionsMenu()?.includes(ApplicationCode.Report) ? (
            <Navigate to={Paths.requestGridPath} />
          ) : (
            <Authorization permission={[ApplicationCode.Report]}>
              <ReportManagementList />
            </Authorization>
          ),
      },
    ],
  },
  ...ReportDistributionDetailRoutes,
  ...SignContractRoutes,
];
